import React, {useContext, useEffect, useMemo, useRef, useState} from "react";

import {Alert, Card, Button, message, Upload, Divider, Table, Space, Popconfirm, Modal, Typography, Tag, Input as AntInput, Select} from 'antd';

import api from "../api";
import {AppContext} from "../../contexts/AppContext";
import {Formik} from "formik";
import {Form, FormItem, Input, SubmitButton} from "formik-antd";
import {
    PoweroffOutlined,
    LoadingOutlined,
    PlusOutlined,
    LockOutlined,
    ApiOutlined,
    DeleteOutlined, SaveOutlined, EditOutlined
} from "@ant-design/icons";
import {isMobile} from "device-detect";
import useCurrentUser from "../helpers/useCurrentUser";
import TimeAgo from "../helpers/TimeAgo";
import FloatLabel from "../helpers/FloatLabel";
import setTitle from "../helpers/setTitle";
import HelpPopover from "../HelpPopover";
import ScopePicker, {BASIC_GROUPS, ADVANCED_GROUPS, ALL_GROUPS, scopeListToMap, groupSummary} from "./ScopePicker";


const ScopesPreview = ({scopeList}) => {
    const safeList = Array.isArray(scopeList) ? scopeList : [];
    const map = useMemo(() => scopeListToMap(safeList), [safeList]);

    if (!safeList.length) return <Typography.Text type="secondary">all</Typography.Text>;

    const pills = ALL_GROUPS
        .map(g => ({group: g, summary: groupSummary(g, map)}))
        .filter(({summary}) => summary.label !== 'None');

    if (!pills.length) return <Typography.Text type="secondary">none</Typography.Text>;

    return (
        <Space size={[4, 4]} wrap style={{maxWidth: 260}}>
            {pills.map(({group, summary}) => (
                <Tag key={group.key} color={summary.color} style={{margin: 0}}>
                    {group.label}: {summary.label.toLowerCase()}
                </Tag>
            ))}
        </Space>
    );
};


export default () => {
    setTitle('Personal Access Tokens')

    const currentUser = useCurrentUser()

    const [loadingPats, setLoadingPats] = useState()
    const [pats, setPats] = useState()

    const [memberships, setMemberships] = useState([])

    const [editingPat, setEditingPat] = useState(null)
    const [editingDraft, setEditingDraft] = useState({scopes: [], note: '', membershipId: null})
    const [savingScopes, setSavingScopes] = useState(false)

    const updateDraft = (patch) => setEditingDraft(prev => ({...prev, ...patch}))

    const load = ()=>{
        setLoadingPats(true)
        api('/api/personal_access_tokens').then(res => {
            setLoadingPats(false)
            setPats(res.data)
        })

        setLoadingAccessGrants(true)
        api('/api/access_grants').then(res => {
            setLoadingAccessGrants(false)
            setAccessGrants(res.data)
        })

        api('/api/personal_access_tokens/memberships').then(res => {
            setMemberships(res.data || [])
        })
    }
    useEffect(()=>{
        load()
    }, [currentUser?.id])

    const membershipOptions = useMemo(() => {
        const opts = memberships
            .slice()
            .sort((a, b) => (a.organization?.title || '').localeCompare(b.organization?.title || ''))
            .map(m => ({
                value: m.id,
                label: `${m.organization?.title} (${m.role_level})`,
            }))
        return [{value: null, label: 'Any organization (all your memberships)'}, ...opts]
    }, [memberships])

    const openEdit = (pat) => {
        setEditingPat(pat)
        setEditingDraft({
            scopes: pat.scope_list || [],
            note: pat.note || '',
            membershipId: pat.membership_id ?? null,
        })
    }

    const closeEdit = () => {
        setEditingPat(null)
        setEditingDraft({scopes: [], note: '', membershipId: null})
    }

    const saveEdit = () => {
        if (!editingPat) return
        setSavingScopes(true)
        api.put(`/api/personal_access_tokens/${editingPat.id}`, {
            personal_access_token: {
                scopes: editingDraft.scopes,
                note: editingDraft.note,
                membership_id: editingDraft.membershipId,
            }
        }).then(() => {
            setSavingScopes(false)
            message.success('Token updated.')
            closeEdit()
            load()
        }).catch(err => {
            setSavingScopes(false)
            const data = err?.response?.data
            if (data?.scopes) {
                message.error(`Scopes: ${data.scopes.join(', ')}`)
            } else {
                message.error('Failed to update token')
            }
        })
    }

    const columns = [
        {
            title: 'Created',
            dataIndex: 'created_at',
            width: 150,
            render: (created_at) => {
                return <TimeAgo date={created_at}/>
            }
        },
        {
            title: 'Note',
            dataIndex: 'note'
        },
        {
            title: 'Organization',
            render: (pat) => {
                if (pat.organization?.title) return <>{pat.organization.title}</>
                return <Typography.Text type="secondary">Any</Typography.Text>
            }
        },
        {
            title: 'Storage Folder',
            render: (pat) => {
                return <>{pat.asset_group?.name}</>
            }
        },
        {
            title: 'Scopes',
            render: (pat) => (
                <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); openEdit(pat); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openEdit(pat);
                        }
                    }}
                    style={{display: 'inline-block', cursor: 'pointer'}}
                >
                    <ScopesPreview scopeList={pat?.scope_list}/>
                </span>
            )
        },
        {
            title: 'Last Used',
            dataIndex: 'last_used_at',
            width: 150,
            render: (last_used_at) => {
                return <TimeAgo date={last_used_at}/>
            }
        },
        {
            key: 'actions',
            width: 160,
            render: (pat)=> {
                const destroy = ()=>{
                    api.delete(`/api/personal_access_tokens/${pat.id}`).then(res => {
                        message.success('Personal Access Token revoked.')
                        load()
                    })
                }

                return (
                    <Space size={4}>
                        <Button icon={<EditOutlined/>} size="small" onClick={() => openEdit(pat)}>Edit</Button>
                        <Popconfirm onConfirm={destroy} title={'Revoke?'}>
                            <Button icon={<DeleteOutlined/>} size="small" danger ghost>Revoke</Button>
                        </Popconfirm>
                    </Space>
                )
            }
        },
    ]

    const [visible, setVisible] = useState()
    const autoFocusInput = useRef(null);

    const [newPat, setNewPat] = useState()

    const [accessGrants, setAccessGrants] = useState()
    const [loadingAccessGrants, setLoadingAccessGrants] = useState(false)
    const accessGrantColumns = [
        {
            title: 'Authorized',
            dataIndex: 'created_at',
            width: 150,
            render: (created_at) => {
                return <TimeAgo date={created_at}/>
            }
        },
        {
            title: 'Application',
            dataIndex: 'application_name'
        },
        {
            title: 'Organization',
            render: ag => {
                return <>{ag.organization?.title}</>
            }
        },
        {
            key: 'actions',
            width: 100,
            render: (ag)=> {
                const destroy = ()=>{
                    api.delete(`/api/access_grants/${ag.id}`).then(res => {
                        message.success('OAuth Authorization revoked.')
                        load()
                    })
                }

                return (
                    <Popconfirm onConfirm={destroy} title={'Revoke?'}>
                        <Button icon={<DeleteOutlined/>} danger ghost>Revoke</Button>
                    </Popconfirm>
                )
            }
        },
    ]

    return (
        (<Card
            title={
                <>
                    <ApiOutlined/> Personal Access Tokens
                    <Button icon={<PlusOutlined/>} type={'primary'} ghost onClick={()=> setVisible(true)} style={{float:'right'}}>Create New</Button>
                </>
            }
            style={{width: isMobile() ? '100%' : 1200, margin:'0 auto', backgroundColor:'white'}}
        >
            {newPat && (
                <Alert
                    showIcon type={'success'}
                    message={
                        <>
                            Personal Access Token created
                        </>
                    }
                    description={
                        <>
                            <div style={{margin: '1em 0'}}>
                                <Typography.Text copyable={{tooltips: 'Copy Token', text: newPat.token}} code>{newPat.token}</Typography.Text>
                            </div>
                            Copy the token and keep it safe like your password; you will only be able to see it once. <HelpPopover code={'personal-access-token'}/>
                        </>
                    }
                    style={{marginBottom:'1em'}}
                />
            )}
            <Formik
                enableReinitialize
                initialValues={{
                    note: '',
                    membership_id: null,
                    scopes: ALL_GROUPS.flatMap(g => g.entities.map(ent => `${ent.key}:write`)),
                }}
                onSubmit={(values, actions) => {
                    api.post('/api/personal_access_tokens', {personal_access_token: values}).then(res => {
                        setVisible(false)
                        setNewPat(res.data)
                        load()
                        actions.resetForm()
                    }).catch(err => {
                        const data = err?.response?.data
                        if (data?.scopes) {
                            message.error(`Scopes: ${data.scopes.join(', ')}`)
                        } else if (data?.membership) {
                            message.error(`Organization: ${data.membership.join(', ')}`)
                        } else {
                            message.error('Failed to create token')
                        }
                    })
                }}
            >
                {({values, setFieldValue, submitForm, dirty, isSubmitting}) => {
                    const issuerIsAdmin = true

                    return (
                        <Modal
                            open={visible}
                            width={640}
                            onCancel={()=>setVisible(false)}
                            title={'New Personal Access Token'}
                            footer={
                                <Space direction={'horizontal'}>
                                    <Button type={'primary'} onClick={submitForm}>
                                        <SaveOutlined/>
                                        Create
                                    </Button>
                                    <Button onClick={() => setVisible(false)}>Cancel</Button>
                                </Space>
                            }
                        >
                            <FormItem name='note'>
                                <FloatLabel label={'Note'} name={'note'} value={values.note} description={'Optional'}>
                                    <Input size={'large'} required name='note' ref={autoFocusInput} autoFocus autoComplete='off'/>
                                </FloatLabel>
                            </FormItem>

                            <div style={{marginTop: 12}}>
                                <Typography.Text strong>Organization</Typography.Text>
                                <div style={{marginTop: 4}}>
                                    <Select
                                        style={{width: '100%'}}
                                        value={values.membership_id ?? null}
                                        options={membershipOptions}
                                        onChange={(val) => setFieldValue('membership_id', val ?? null)}
                                        placeholder="Any organization"
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </div>
                                <Typography.Text type="secondary" style={{fontSize: 12}}>
                                    Restrict this token to one organization, or leave as "Any" to use it across all your memberships.
                                </Typography.Text>
                            </div>

                            <Divider style={{margin: '12px 0'}}>Scopes</Divider>

                            <ScopePicker
                                value={values.scopes}
                                isAdmin={issuerIsAdmin}
                                onChange={(list) => setFieldValue('scopes', list)}
                            />
                        </Modal>
                    );
                }}
            </Formik>

            <Modal
                open={!!editingPat}
                width={640}
                onCancel={closeEdit}
                title="Edit Personal Access Token"
                footer={
                    <Space>
                        <Button type="primary" icon={<SaveOutlined/>} loading={savingScopes} onClick={saveEdit}>Save</Button>
                        <Button onClick={closeEdit}>Cancel</Button>
                    </Space>
                }
            >
                {editingPat && (() => {
                    const issuerIsAdmin = true

                    return (
                        <>
                            <FloatLabel label="Note" value={editingDraft.note} description="Optional">
                                <AntInput
                                    size="large"
                                    value={editingDraft.note}
                                    onChange={(e) => updateDraft({note: e.target.value})}
                                    autoComplete="off"
                                />
                            </FloatLabel>

                            <div style={{marginTop: 12}}>
                                <Typography.Text strong>Organization</Typography.Text>
                                <div style={{marginTop: 4}}>
                                    <Select
                                        style={{width: '100%'}}
                                        value={editingDraft.membershipId ?? null}
                                        options={membershipOptions}
                                        onChange={(val) => updateDraft({membershipId: val ?? null})}
                                        placeholder="Any organization"
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                </div>
                            </div>

                            <Divider style={{margin: '12px 0'}}>Scopes</Divider>

                            <ScopePicker
                                value={editingDraft.scopes}
                                isAdmin={issuerIsAdmin}
                                onChange={(scopes) => updateDraft({scopes})}
                            />
                        </>
                    );
                })()}
            </Modal>

            <Table
                scroll={{x: true}}
                size={'small'}
                columns={columns}
                rowKey={record => record.id}
                dataSource={pats}
                loading={loadingPats}
                pagination={false}
            />
            <Divider>Authorized OAuth Applications</Divider>
            <Table
                scroll={{x: true}}
                size={'small'}
                columns={accessGrantColumns}
                rowKey={record => record.id}
                dataSource={accessGrants}
                loading={loadingAccessGrants}
                pagination={false}
            />
            <p style={{marginTop:'1em'}}>
                API documentation here: <a href={'https://docs.mediagraph.io'} target={'_blank'}>docs.mediagraph.io</a>
            </p>
        </Card>)
    );
}
